import { useState, useEffect, createContext, useContext } from 'react';
import type { User as FirebaseUser } from 'firebase/auth';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  signOut,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { auth } from '../../lib/firebase.js';
import { apiClient, ApiError } from '../../lib/api-client.js';
import type { User } from '@ridehub/shared';

export const isDevAuthBypass = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';

interface AuthContextValue {
  user: FirebaseUser | null;
  dbUser: User | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string, nickname: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuthState(): AuthContextValue {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [dbUser, setDbUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // ローカル開発用バイパス: Firebaseを使わずDBの実ユーザーをセット
    if (isDevAuthBypass) {
      const devUser = { uid: 'dev-user-uid', email: 'dev@example.com' } as FirebaseUser;
      setUser(devUser);
      apiClient
        .get<User>('/auth/me')
        .then((realUser) => {
          setDbUser(realUser);
          setLoading(false);
        })
        .catch(() => setLoading(false));
      return;
    }

    // 過去のリダイレクトセッションがある場合の後方互換処理
    // (signInWithPopupに移行済みのため新規には発生しない)
    getRedirectResult(auth)
      .then(async (result) => {
        if (result) {
          await ensureUserRegistered(result.user);
        }
      })
      .catch(() => {});

    // 非同期API呼び出し中に新しいコールバックが来た場合、古い結果を捨てるためのカウンタ
    let callbackCount = 0;

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      const myCount = ++callbackCount;
      setUser(firebaseUser);
      if (firebaseUser) {
        try {
          const userData = await apiClient.get<User>('/auth/me', { requireAuth: true });
          if (myCount !== callbackCount) return; // 新しいコールバックが来ていたら破棄
          setDbUser(userData);
        } catch (err) {
          // 初回Googleログイン時のレース競合: ensureUserRegisteredより先にonAuthStateChangedが
          // 発火してユーザーがDBに存在しない場合、登録を試みてからリトライする
          if (err instanceof ApiError && err.status === 401) {
            await ensureUserRegistered(firebaseUser);
            try {
              const userData = await apiClient.get<User>('/auth/me', { requireAuth: true });
              if (myCount !== callbackCount) return;
              setDbUser(userData);
            } catch {
              if (myCount !== callbackCount) return;
              setDbUser(null);
            }
          } else {
            if (myCount !== callbackCount) return;
            setDbUser(null);
          }
        }
      } else {
        setDbUser(null);
      }
      if (myCount !== callbackCount) return;
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    try {
      // ポップアップで認証（ページリロード不要、レース競合なし）
      const result = await signInWithPopup(auth, provider);
      await ensureUserRegistered(result.user);
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code;
      // ポップアップがブロックされた場合はリダイレクトにフォールバック
      if (code === 'auth/popup-blocked') {
        await signInWithRedirect(auth, provider);
        return;
      }
      // ユーザーがポップアップを閉じた場合は何もしない
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
        return;
      }
      throw err;
    }
  };

  const signInWithEmail = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const signUpWithEmail = async (email: string, password: string, nickname: string) => {
    const credential = await createUserWithEmailAndPassword(auth, email, password);
    await apiClient.post('/auth/register', {
      firebaseUid: credential.user.uid,
      email: credential.user.email,
      nickname,
    });
  };

  const logout = async () => {
    await signOut(auth);
    setDbUser(null);
  };

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  return {
    user,
    dbUser,
    loading,
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    logout,
    resetPassword,
  };
}

async function ensureUserRegistered(firebaseUser: FirebaseUser) {
  try {
    await apiClient.post('/auth/register', {
      firebaseUid: firebaseUser.uid,
      email: firebaseUser.email,
      nickname: firebaseUser.displayName ?? 'ユーザー',
    });
  } catch {
    // 既存ユーザーの場合はConflictErrorになるが無視
  }
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
